import React, {useCallback, useEffect, useState} from "react";
import {Button, Col, ControlLabel, FormControl, FormGroup, Modal, Row} from "react-bootstrap";
import Select from "react-select";
import {defaultDateFormat, select} from "../../../../common/commonHandlers";
import {DateRangePicker} from "react-dates";
import moment from "moment";
import {debounce} from "throttle-debounce";

const AdminTransactionsFiltersModal = ({onHide, onSave, prevFilters, searchForCustomer, searchForInvoices, searchForClients}) => {
    const [filters, setFilters] = useState(prevFilters)
    const [onFocusChange, setOnFocusChange] = useState({created: null, available: null})
    const [customerOptions, setCustomerOptions] = useState([])
    const [invoiceOptions, setInvoiceOptions] = useState([])
    const [clientOptions, setClientOptions] = useState([])

    const transactionTypes = [
        {value: "payout", label: "Payout"},
        {value: "payment", label: "Payment"},
        {value: "charge", label: "Charge"},
        {value: "refund", label: "Refund"},
        {value: "payment_refund", label: "Payment refund"},
        {value: "transfer_refund", label: "Transfer refund"},
        {value: "refund_failure", label: "Refund failure"},
        {value: "payout_cancel", label: "Payout cancel"},
        {value: "payout_failure", label: "Payout failure"},
        {value: "application_fee", label: "Application fee"},
        {value: "application_fee_refund", label: "Application fee refund"},
        {value: "stripe_fee", label: "Convenience fee"}
    ]

    const statuses = [
        {value: "pending", label: "Pending"},
        {value: "available", label: "Available"}
    ]

    const customerDelayedSearch = useCallback(debounce(1000, (text, filters) => customerSearch(text, filters)), [])
    const invoiceDelayedSearch = useCallback(debounce(1000, (text, filters) => invoiceSearch(text, filters)), [])
    const clientDelayedSearch = useCallback(debounce(1000, (text, filters) => clientSearch(text, filters)), [])

    const customerSearch = (text, filters) => {
        if (text && text.length > 0) {
            searchForCustomer(text, (res) => {
                const result = filters?.customer_ids && filters?.customer_ids.length > 0 ? res.concat(filters?.customer_ids) : res
                const uniqueResult = Array.from(new Set(result))
                setCustomerOptions(uniqueResult)
            })
        }
    }

    const invoiceSearch = (text, filters) => {
        if (text && text.length > 0) {
            searchForInvoices(text, (res) => {
                const result = filters?.invoice_ids && filters?.invoice_ids.length > 0 ? res.concat(filters?.invoice_ids) : res
                const uniqueResult = Array.from(new Set(result))
                setInvoiceOptions(uniqueResult)
            })
        }
    }

    const clientSearch = (text, filters) => {
        if (text && text.length > 0) {
            searchForClients(text, (res) => {
                const result = filters?.client_ids && filters?.client_ids.length > 0 ? res.concat(filters?.client_ids) : res
                const uniqueResult = Array.from(new Set(result))
                setClientOptions(uniqueResult)
            })
        }
    }

    const selectForm = (options, name, placeholder, isMulti = false, onInputChange = null) => {
        let value = filters[name]

        if (Array.isArray(value)) {value = value.map(v => v.value)}

        return <FormGroup>
            <ControlLabel>{placeholder}</ControlLabel>
            <Select className="Select"
                    classNamePrefix={`select-${name} select_list`}
                    value={select(options, value)}
                    options={options}
                    placeholder={placeholder}
                    isClearable
                    isMulti={isMulti}
                    filterOption={(option, inputValue) => {return option}}
                    onInputChange={onInputChange ? (e) => onInputChange(e, filters) : undefined}
                    onChange={(e) => setFilters({...filters, [name]: isMulti ? e : e?.value})}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
        </FormGroup>
    }

    const textForm = (type, name, placeholder) => {
        return <FormGroup>
            <ControlLabel>{placeholder}</ControlLabel>
            <FormControl
                type={type}
                placeholder={placeholder}
                name={name}
                value={filters[name]}
                onChange={(e) => setFilters({...filters, [name]: e?.target?.value})}
            />
        </FormGroup>
    }

    const dateForm = (label, name_from, name_to, type) => {
        return <FormGroup>
            <ControlLabel>{label}</ControlLabel>
            <DateRangePicker
                startDate={filters[name_from] && moment(filters[name_from]) || ""}
                startDateId={'P From'}
                endDate={filters[name_to] && moment(filters[name_to]) || ""}
                endDateId={'P To'}
                keepOpenOnDateSelect={true}
                showClearDates={true}
                reopenPickerOnClearDate={true}
                onDatesChange={({startDate, endDate}) => {
                    setFilters({
                        ...filters,
                        [name_from]: startDate ? moment(startDate).format(defaultDateFormat) : startDate,
                        [name_to]: endDate ?  moment(endDate).format(defaultDateFormat) : endDate
                    })
                    if (startDate && endDate) {
                        setOnFocusChange({...onFocusChange, [type]: null})
                    }
                }}
                focusedInput={onFocusChange[type]}
                onFocusChange={(focusedInput) => setOnFocusChange({...onFocusChange, [type]: focusedInput})}
                isOutsideRange={() => { return false}}
                displayFormat={defaultDateFormat}
            />
        </FormGroup>
    }

    useEffect(() => {
        if (prevFilters.customer_ids && prevFilters.customer_ids.length > 0) {
            setCustomerOptions(prevFilters.customer_ids)
        }
        if (prevFilters.invoice_ids && prevFilters.invoice_ids.length > 0) {
            setInvoiceOptions(prevFilters.invoice_ids)
        }
        if (prevFilters.client_ids && prevFilters.client_ids.length > 0) {
            setClientOptions(prevFilters.client_ids)
        }
    }, [])

    return <Modal
        show={true}
        onHide={onHide}
        backdrop={false}
        className={"heightAuto maxHeight90 modalLeadsFilterBody fontIOS"}
    >
        <Modal.Header>
            <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{maxHeight: '450px', overflow: 'auto'}}>
            <Row>
                <Col xs={12}>
                    {dateForm("Created Date", "created_from", "created_to", "created")}
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    {dateForm("Available Date", "available_from", "available_to", "available")}
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    {selectForm(customerOptions, "customer_ids", "Customer", true, customerDelayedSearch)}
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    {selectForm(invoiceOptions, "invoice_ids", "Invoice", true, invoiceDelayedSearch)}
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    {selectForm(clientOptions, "client_ids", "Client", true, clientDelayedSearch)}
                </Col>
            </Row>
            <Row className="mt-15">
                <Col md={6}>{selectForm(statuses, "status", "Status")}</Col>
                <Col md={6}>{selectForm(transactionTypes, "type", "Type")}</Col>
            </Row>
            <Row>
                <Col xs={6} md={3}>{textForm("number", "amount_from", "Amount from")}</Col>
                <Col xs={6} md={3}>{textForm("number", "amount_to", "Amount to")}</Col>
                <Col xs={6} md={3}>{textForm("number", "fee_amount_from", "Fee amount from")}</Col>
                <Col xs={6} md={3}>{textForm("number", "fee_amount_to", "Fee amount to")}</Col>
            </Row>
            <Row>
                <Col xs={6} md={3}>{textForm("number", "net_from", "Net from")}</Col>
                <Col xs={6} md={3}>{textForm("number", "net_to", "Net to")}</Col>
            </Row>
        </Modal.Body>
        <Modal.Footer>
            <Button
                bsStyle="warning"
                onClick={onHide}
            >
                Close
            </Button>
            <Button
                bsStyle="success"
                className="ml-7"
                onClick={() => {
                    onSave(filters)
                    onHide()
                }}
            >
                Save
            </Button>
        </Modal.Footer>
    </Modal>
}

export default AdminTransactionsFiltersModal;